import { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { uploadFileResumable } from "@/services/storage";
import { fmtKB } from "@/lib/imageSpecs";
import { cn } from "@/lib/utils";

// Photos + videos for a product. Unlimited items, multi-file upload (button or drag-and-drop),
// reorder, pick the cover (first photo), remove, or add by URL.
// value: [{ type: "image"|"video", url, name?, w?, h?, bytes? }]
// onChange receives a new list OR an updater fn (cur => next) — pass a React state setter
// (or something that supports updaters) so parallel uploads never overwrite each other.
export const PRODUCT_PHOTO = { w: 1200, h: 1200, ratio: "1:1 (square)", maxKB: 400 };
const MAX_IMAGE = 10 * 1048576;   // 10 MB
const MAX_VIDEO = 100 * 1048576;  // 100 MB
const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(\?|$)/i;
const YT = /youtu\.?be/i;
const safeName = (n) => String(n || "file").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").slice(-80);
const dims = (file) => new Promise((res) => {
  const url = URL.createObjectURL(file);
  const done = (w, h) => { URL.revokeObjectURL(url); res({ w, h }); };
  if (file.type.startsWith("video/")) {
    const v = document.createElement("video");
    v.preload = "metadata"; v.onloadedmetadata = () => done(v.videoWidth, v.videoHeight); v.onerror = () => done(0, 0); v.src = url;
  } else { const im = new Image(); im.onload = () => done(im.naturalWidth, im.naturalHeight); im.onerror = () => done(0, 0); im.src = url; }
});

export function MediaManager({ value = [], onChange, folder = "app_content/products", compact = false, testId = "media" }) {
  const inputRef = useRef(null);
  const [jobs, setJobs] = useState({}); // name -> pct
  const [drag, setDrag] = useState(false);
  const [url, setUrl] = useState("");
  const list = Array.isArray(value) ? value : [];

  const addFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    for (const f of arr) {
      const isVid = f.type.startsWith("video/");
      if (!isVid && !f.type.startsWith("image/")) { toast.error(`${f.name}: only photos or videos`); continue; }
      if (f.size > (isVid ? MAX_VIDEO : MAX_IMAGE)) { toast.error(`${f.name} is ${fmtKB(f.size)} — limit is ${isVid ? "100 MB" : "10 MB"}`); continue; }
      const key = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}_${f.name}`;
      setJobs((j) => ({ ...j, [key]: 0 }));
      (async () => {
        try {
          const d = await dims(f);
          const u = await uploadFileResumable(`${folder}/${key.replace(f.name, safeName(f.name))}`, f, (p) => setJobs((j) => ({ ...j, [key]: p })));
          const item = { type: isVid ? "video" : "image", url: u, name: f.name, w: d.w || null, h: d.h || null, bytes: f.size };
          onChange((cur) => [...(Array.isArray(cur) ? cur : []), item]);
          if (!isVid && d.w && (d.w < 800 || Math.abs(d.w / d.h - 1) > 0.25)) toast.message(`${f.name}: ${d.w}×${d.h}. Square ${PRODUCT_PHOTO.w}×${PRODUCT_PHOTO.h} looks best.`);
        } catch (e) {
          toast.error(e?.code === "storage/unauthorized" ? "Upload refused by Firebase Storage. Publish the Storage rules in the Firebase console, then try again." : `${f.name}: ${e?.response?.data?.detail || e.message}`);
        } finally { setJobs((j) => { const n = { ...j }; delete n[key]; return n; }); }
      })();
    }
  };
  const addUrl = () => {
    const u = url.trim();
    if (!/^https?:\/\//.test(u)) return toast.error("Paste a full link starting with https://");
    onChange([...list, { type: VIDEO_EXT.test(u) || YT.test(u) ? "video" : "image", url: u }]);
    setUrl("");
  };
  const move = (i, d) => { const a = [...list]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
  const makeCover = (i) => { const a = [...list]; const [m] = a.splice(i, 1); onChange([m, ...a]); };
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const pending = Object.entries(jobs);
  const firstImg = list.findIndex((m) => m.type !== "video");

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className={cn("grid gap-2", compact ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-5")}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}>
        {list.map((m, i) => (
          <div key={`${m.url}-${i}`} className="group relative aspect-square rounded-lg overflow-hidden border border-border bg-secondary/40" data-testid={`${testId}-item-${i}`}>
            {m.type === "video"
              ? (YT.test(m.url) ? <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground p-1 text-center"><Icons.Youtube className="h-6 w-6 text-red-600" /></div>
                : <video src={m.url} className="w-full h-full object-cover" muted preload="metadata" />)
              : <img src={m.url} alt="" className="w-full h-full object-cover" />}
            {m.type === "video" && <span className="absolute top-1 left-1 rounded bg-black/60 text-white text-[9px] px-1 flex items-center gap-0.5"><Icons.Play className="h-2.5 w-2.5" />Video</span>}
            {i === firstImg && <span className="absolute top-1 left-1 rounded bg-primary text-primary-foreground text-[9px] font-semibold px-1">Cover</span>}
            {!compact && m.w ? <span className="absolute bottom-1 left-1 rounded bg-black/55 text-white text-[9px] px-1">{m.w}×{m.h}{m.bytes ? ` · ${fmtKB(m.bytes)}` : ""}</span> : null}
            <div className="absolute inset-x-0 top-0 flex justify-end gap-0.5 p-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              {i !== firstImg && m.type !== "video" && <button type="button" title="Make cover" onClick={() => makeCover(i)} className="h-6 w-6 rounded bg-white/90 text-foreground flex items-center justify-center"><Icons.Star className="h-3 w-3" /></button>}
              {i > 0 && <button type="button" title="Move left" onClick={() => move(i, -1)} className="h-6 w-6 rounded bg-white/90 text-foreground flex items-center justify-center"><Icons.ChevronLeft className="h-3 w-3" /></button>}
              {i < list.length - 1 && <button type="button" title="Move right" onClick={() => move(i, 1)} className="h-6 w-6 rounded bg-white/90 text-foreground flex items-center justify-center"><Icons.ChevronRight className="h-3 w-3" /></button>}
              <button type="button" title="Remove" onClick={() => remove(i)} className="h-6 w-6 rounded bg-white/90 text-red-600 flex items-center justify-center" data-testid={`${testId}-remove-${i}`}><Icons.X className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {pending.map(([k, p]) => (
          <div key={k} className="aspect-square rounded-lg border border-border bg-secondary/40 flex flex-col items-center justify-center gap-1.5 p-2">
            <Icons.Loader2 className="h-4 w-4 animate-spin text-primary" /><span className="text-[10px] text-muted-foreground">{p}%</span>
            <div className="h-1 w-full rounded-full bg-border overflow-hidden"><div className="h-full bg-primary" style={{ width: `${p}%` }} /></div>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testId}-add`}
          className={cn("aspect-square rounded-lg border border-dashed flex flex-col items-center justify-center gap-1 text-[11px] text-muted-foreground hover:border-primary hover:text-primary transition-colors", drag ? "border-primary bg-primary/5" : "border-border")}>
          <Icons.ImagePlus className="h-5 w-5" />{compact ? "Add" : "Add photos / videos"}
        </button>
        <input ref={inputRef} type="file" multiple accept="image/*,video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid={`${testId}-input`} />
      </div>
      {!compact && (
        <>
          <div className="flex gap-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())} placeholder="…or paste a photo / video / YouTube link" className="h-8 text-xs" data-testid={`${testId}-url`} />
            <Button type="button" size="sm" variant="outline" className="h-8" onClick={addUrl}>Add link</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            <b className="text-foreground">Photos:</b> square {PRODUCT_PHOTO.w} × {PRODUCT_PHOTO.h} px, white or plain background, JPG/WebP, under {PRODUCT_PHOTO.maxKB} KB (max 10 MB). <b className="text-foreground">Videos:</b> MP4, under 30 seconds, max 100 MB. Drop several files at once; the first photo is the cover.
          </p>
        </>
      )}
    </div>
  );
}

// Product doc fields derived from the media list (what brugo.in reads today: image_url + images[]).
export function mediaFields(media = []) {
  const images = media.filter((m) => m.type !== "video").map((m) => m.url);
  const videos = media.filter((m) => m.type === "video").map((m) => m.url);
  return { media, images, videos, image_url: images[0] || "", image: images[0] || "" };
}

// Build a media list from an existing product doc (older docs only have image/images).
export function mediaFromDoc(p = {}) {
  if (Array.isArray(p.media) && p.media.length) return p.media;
  const imgs = [...new Set([p.image_url, p.image, ...(p.images || p.image_urls || [])].filter(Boolean))];
  return [...imgs.map((url) => ({ type: "image", url })), ...(p.videos || []).map((url) => ({ type: "video", url }))];
}
